import {
  Popover,
  Stack,
  IconButton,
  CardHeader,
  Card,
  CardContent,
  Divider,
  Box,
} from '@mui/material';
import EditIconOutlined from '@mui/icons-material/EditOutlined';
import DeleteIconOutlined from '@mui/icons-material/DeleteOutlined';
import moment from 'moment';
import { PopoverEvent } from '@src/types/Events';
import Brightness1Icon from '@mui/icons-material/Brightness1';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Dispatch, SetStateAction } from 'react';

interface EventPopoverProps {
  anchorEl: HTMLElement | null;
  onEdit: () => void;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  popoverEvent: PopoverEvent | null;
}

export default function EventPopover({
  anchorEl,
  onEdit,
  onClose,
  setOpen,
  popoverEvent,
}: EventPopoverProps) {
  const isPopoverOpen = Boolean(anchorEl);
  const id = isPopoverOpen ? 'simple-popover' : undefined;
  const cardSectionSx = {
    p: 1,
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <>
      <Popover
        open={isPopoverOpen}
        id={id}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Card
          sx={{
            width: 375,
            p: 0,
          }}
        >
          <CardHeader
            avatar={
              <Brightness1Icon sx={{ color: popoverEvent?.backgroundColor }} />
            }
            action={
              <>
                <IconButton onClick={onEdit} color='primary'>
                  <EditIconOutlined color='action' />
                </IconButton>
                <IconButton onClick={() => setOpen(true)} color='error'>
                  <DeleteIconOutlined color='action' />
                </IconButton>
              </>
            }
            title={popoverEvent?.title}
            sx={{
              p: 1,
              bgcolor: '#4d5256',
            }}
          />
          <CardContent sx={{ p: 0, border: '1px solid #4d5256' }}>
            <Stack>
              <Stack direction='row' spacing={1} sx={cardSectionSx}>
                <Box flex={1}>
                  <AccessAlarmIcon sx={{ fontSize: '16px', mr: 1 }} />
                  {moment.utc(popoverEvent?.start).format('DD MMM, YYYY')}
                </Box>
                <Box flex={1}>
                  {moment.utc(popoverEvent?.start).format('h:mmA')} -{' '}
                  {moment.utc(popoverEvent?.end).format('h:mmA')}
                </Box>
              </Stack>
              {popoverEvent?.recurrenceFreq && (
                <Stack direction='row' spacing={1} sx={cardSectionSx}>
                  <Box flex={1}>
                    <EventRepeatIcon sx={{ fontSize: '16px', mr: 1 }} />
                    <span>Recurrence</span>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      textTransform: 'capitalize',
                    }}
                  >
                    {popoverEvent?.recurrenceFreq}
                  </Box>
                </Stack>
              )}
              <Divider
                sx={{
                  my: 1,
                  border: '1px solid',
                }}
              />
              <Stack direction='row' spacing={1} sx={cardSectionSx}>
                <Box flex={1}>
                  <ArrowForwardIcon sx={{ fontSize: '16px', mr: 1 }} />
                  <span>Start Command</span>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textOverflow: 'ellipsis',
                  }}
                >
                  {popoverEvent?.startCommands?.join(', ')}
                </Box>
              </Stack>
              <Stack direction='row' spacing={1} sx={cardSectionSx}>
                <Box flex={1}>
                  <ArrowBackIcon sx={{ fontSize: '16px', mr: 1 }} />
                  <span>Stop Command</span>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    textOverflow: 'ellipsis',
                  }}
                >
                  {popoverEvent?.stopCommands?.join(', ')}
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
}
